import { useState, useEffect } from 'react';

/**
 * Same phone display as campaign detail tables (+1{national digits}).
 */
function getDisplayedPhone(phone) {
  const raw = String(phone || '');
  const digits = raw.replace(/\D/g, '');
  if (!digits) return raw;
  const national = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  return national ? `+1${national}` : raw;
}

/**
 * Format customer date-added the same way campaign detail tables display it (MM/DD/YYYY).
 */
function formatCustomerDateAdded(dateString) {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  } catch (error) {
    return 'N/A';
  }
}

/** Debounce customer search input (large campaign lists). */
export function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Client-side filter for campaign customer lists (name, phone/partial/area code, date added).
 * Searches the full list — callers should paginate the returned array.
 */
export function filterCampaignCustomers(customers, searchTerm) {
  const list = Array.isArray(customers) ? customers : [];
  const q = String(searchTerm || '').trim().toLowerCase();
  if (!q) return list;

  const qDigits = q.replace(/\D/g, '');
  const isNumericOnlyQuery = qDigits.length > 0 && qDigits === q.replace(/\s/g, '');
  const shouldSearchDates = !isNumericOnlyQuery || /[/-]/.test(q);

  return list.filter((customer) => {
    const name = String(customer?.name || '').toLowerCase();
    if (name.includes(q)) return true;

    const phone = String(customer?.phone_number || '');
    const phoneDisplay = getDisplayedPhone(phone).toLowerCase();
    if (phoneDisplay.includes(q)) return true;
    if (phone.toLowerCase().includes(q)) return true;

    const phoneDigitVariants = [
      phone.replace(/\D/g, ''),
      phoneDisplay.replace(/\D/g, ''),
    ].filter(Boolean);
    if (qDigits) {
      for (const digits of phoneDigitVariants) {
        if (digits.includes(qDigits)) return true;
      }
    }

    const dateCandidates = [
      customer?.created_at,
      customer?.date_added,
      customer?.pending_date_added,
    ];
    if (shouldSearchDates) {
      for (const raw of dateCandidates) {
        const dateFormatted = formatCustomerDateAdded(raw).toLowerCase();
        if (dateFormatted !== 'n/a' && dateFormatted.includes(q)) return true;
        if (String(raw || '').toLowerCase().includes(q)) return true;
      }
    }

    return false;
  });
}
